import { IDirectionDepricated, IMLResult } from "../../types/types";
import { IDeviceBasicCompDepricated } from "./deviceTypes";
import {
  createNormalVectorPath,
  generateRandomString,
  getCenterOfBoundingBox,
  getDirectionFromMlResultItem,
  getMcBimInstanceFromMlResult,
  getPixPathForCeilingGrid,
  getRotatedBoundingBox,
  getSqaureAroundCoordinate,
  PartialMcBimSpace,
} from "../../utils";
import {
  calculateTileSizeFromBoundingBox,
  DEVICE_DISCIPLINE_MAPPING,
  DeviceCategoryEnum,
  DEVICES_TYPES_SETTINGS,
  DisciplineEnum,
  getDeeplyNestedValue,
  getDeviceCategoryForMcBimItem,
  getMcbimItemClassificationInput,
  isReturnAirterminal,
  isSupplyAirTerminal,
  RIGHT_CLICK_DEVICE_MENU_ENUM,
} from "@multiconsult-group/multiplacer-core";
import { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import {
  IBoundingBox,
  IDeviceType,
  IMcBimItem,
  IRoomSummary,
  IRoomsSummaryHashmap,
} from "@backend/src/shared/schemas/schemas";
import { IDevice, IDirection, IMLPredictDevice } from "@multiconsult-group/multiplacer-core";

export const getWorldZMFromFloorAndCeilingHeight = (
  floorElevationM: number | undefined,
  ceilingHeightMm: number | undefined
): number => {
  return (floorElevationM ?? 0) + (ceilingHeightMm ?? 0) / 1000;
};

export const withWorldZInCoordinate = (
  coordinate: number[] | undefined,
  floorElevationM: number | undefined,
  ceilingHeightMm: number | undefined
): [number, number, number] => {
  const x = coordinate?.[0] ?? 0;
  const y = coordinate?.[1] ?? 0;
  const z = getWorldZMFromFloorAndCeilingHeight(floorElevationM, ceilingHeightMm);
  return [x, y, z];
};

interface IAddDevicesArgs {
  mcbimSpace: PartialMcBimSpace;
  room?: IRoomSummary;
  mlResults: IMLResult[];
  mcBimItemsInStandardSpace: IMcBimItem[];
  supplyAirFlow: number | undefined;
  returnAirFlow: number | undefined;
  compCodePath: string;
  projectNo: number;
}

interface IConvertMcBimToDeviceArgs {
  mcBimItem: IMcBimItem;
  roomNumber: string;
  roomName?: string;
  compCodePath: string;
  projectNo: number;
  ceilingHeight: number;
}

export const convertMcBimItemToDevice = (args: IConvertMcBimToDeviceArgs): IDevice | undefined => {
  const classificationInput = getMcbimItemClassificationInput({
    mcbimItem: args.mcBimItem,
    compCodePath: args.compCodePath,
  });
  const deviceCategory = getDeviceCategoryForMcBimItem(classificationInput);
  if (deviceCategory == null) {
    return undefined;
  }
  return {
    compCode: getDeeplyNestedValue(args.mcBimItem, args.compCodePath) ?? "",
    description: args.mcBimItem.type?.name ?? "",
    boundingBox: args.mcBimItem.bbox as IBoundingBox,
    coordinate: getCenterOfBoundingBox(args.mcBimItem.bbox as IBoundingBox),
    direction: [0, 0],
    deviceCategory: getDeviceCategoryForMcBimItem(classificationInput) ?? DeviceCategoryEnum.DEFAULT_DEVICE,
    discipline: DEVICE_DISCIPLINE_MAPPING[deviceCategory],
    projectNo: String(args.projectNo),
    roomName: args.roomName,
    roomNumber: args.roomNumber,
    floor: args.mcBimItem.buildingStory?.name ?? "",
    bottomEdgeHeightAboveFloorMm: args.ceilingHeight ?? 2700,
  };
};

interface IGetDeviceBasicCompFromMlResultArgs {
  mlResult: IMLResult;
  spaceSupplyAirFlow: number;
  spaceReturnAirFlow: number;
  mcBimItemsInStandardSpace: IMcBimItem[];
  compCodePath: string;
}

export const getDeviceBasicCompFromMlResult = (
  args: IGetDeviceBasicCompFromMlResultArgs
): IDeviceBasicCompDepricated => {
  const mcbimInstance = getMcBimInstanceFromMlResult(
    args.mlResult,
    args.mcBimItemsInStandardSpace,
    args.compCodePath as string
  );

  let device: IDeviceBasicCompDepricated = {
    compCode: (mcbimInstance?.psets?.NONS_Reference.RefCompType as string) ?? "-",
    description: (mcbimInstance?.type?.name as string) ?? "-",
    productName: (mcbimInstance?.psets?.NRH_PG_Info.Mengdetype as string) ?? "-",
    boundingBox: args.mlResult.bounding_box as IBoundingBox,
    coordinate:
      args.mlResult.bounding_box != null
        ? getCenterOfBoundingBox(args.mlResult.bounding_box)
        : [0, 0],
    direction: getDirectionFromMlResultItem(args.mlResult) as IDirection,
    deviceCategory: args.mlResult.device_type,
    grid: args.mlResult.grid,
    discipline: DEVICE_DISCIPLINE_MAPPING[args.mlResult.device_type] ?? DisciplineEnum.UNKNOWN,
  };

  if (mcbimInstance == null) {
    return device;
  }

  const classificationInput = getMcbimItemClassificationInput({
    mcbimItem: mcbimInstance,
    compCodePath: args.compCodePath,
  });

  if (isSupplyAirTerminal(classificationInput)) {
    const supplyAirFlow = args.spaceSupplyAirFlow;
    const nrOfSupplyAirTerminalsInSpace = args.mcBimItemsInStandardSpace.filter((mcbimItem) =>
      isSupplyAirTerminal(
        getMcbimItemClassificationInput({
          mcbimItem,
          compCodePath: args.compCodePath,
        })
      )
    ).length;
    device = {
      ...device,
      supplyAirFlow: supplyAirFlow / nrOfSupplyAirTerminalsInSpace,
    };
  } else if (isReturnAirterminal(classificationInput)) {
    const returnAirFlow = args.spaceReturnAirFlow;
    const nrOfReturnAirTerminalsInSpace = args.mcBimItemsInStandardSpace.filter((mcbimItem) =>
      isReturnAirterminal(
        getMcbimItemClassificationInput({
          mcbimItem,
          compCodePath: args.compCodePath,
        })
      )
    ).length;
    device = {
      ...device,
      returnAirFlow: returnAirFlow / nrOfReturnAirTerminalsInSpace,
    };
  }
  return device;
};

const rightClicMenuWithoutRotate = Object.entries(RIGHT_CLICK_DEVICE_MENU_ENUM).reduce(
  (acc, [key, value]) => {
    if (value === RIGHT_CLICK_DEVICE_MENU_ENUM.ROTATE) {
      return acc;
    }
    acc.push(value);
    return acc;
  },
  [] as string[]
);

const rightClickMenuWithRotate = Object.values(RIGHT_CLICK_DEVICE_MENU_ENUM);

export type IDeviceWithMovable = IDevice & { isMoveable: boolean };
interface IArgsConvertDevice {
  devices: IDevice[];
  typeDevices: IDeviceType[];
  selectedDeviceId: string | null;
  hideDevices: boolean;
  showTrueSize: boolean;
  isMoveable: boolean;
  rooms?: IRoomsSummaryHashmap;
}

/**
 * Standalone single-device converter.
 * Everything it needs is passed in, so it can be used outside convertDevicesToPixPolygons.
 */
interface IArgsConvertSingleDevice {
  deviceItem: IDevice;
  typeDevices: IDeviceType[];
  selectedDeviceId: string | null;
  showTrueSize: boolean;
  isMoveable: boolean;
  room?: IRoomsSummaryHashmap[string];
}

const CEILING_TILE_SIZE_M = 0.6;

const createSchematicTilePolygon = (
  coordinate: [number, number],
  direction: [number, number],
  tileSize?: number[]
): number[][] => {
  const spans =
    Array.isArray(tileSize) && tileSize.length === 2 && tileSize.every((value) => value > 0)
      ? tileSize
      : [1, 1];

  const longSpan = Math.max(spans[0], spans[1]);
  const shortSpan = Math.min(spans[0], spans[1]);
  const directionLength = Math.hypot(direction[0], direction[1]);
  const unitDirection =
    directionLength > 0
      ? ([direction[0] / directionLength, direction[1] / directionLength] as const)
      : ([1, 0] as const);
  const normal = [-unitDirection[1], unitDirection[0]] as const;
  const halfLength = (longSpan * CEILING_TILE_SIZE_M) / 2;
  const halfWidth = (shortSpan * CEILING_TILE_SIZE_M) / 2;

  const corners = [
    [-halfLength, -halfWidth],
    [halfLength, -halfWidth],
    [halfLength, halfWidth],
    [-halfLength, halfWidth],
  ].map(([along, across]) => [
    coordinate[0] + unitDirection[0] * along + normal[0] * across,
    coordinate[1] + unitDirection[1] * along + normal[1] * across,
  ]);

  return [...corners, corners[0]];
};

export const convertSingleDeviceToPixPolygon = (
  args: IArgsConvertSingleDevice
): IPixPolygon | null => {
  const { deviceItem, typeDevices, selectedDeviceId, showTrueSize, isMoveable, room } = args;

  if (deviceItem == null) {
    return null;
  }

  const isLocked = room?.multiplacerCeilingDevicesLocked?.bool === true;
  const deviceType = typeDevices.find((t) => t.compCode === deviceItem.compCode);

  // Build default polygon (square around coordinate) unless missing required geometry inputs
  let resultPolygon: number[][] | null = null;
  if (deviceItem.coordinate != null && deviceItem.grid == null && deviceItem.direction != null) {
    const schematicTileSize =
      deviceType?.boundingBox != null
        ? calculateTileSizeFromBoundingBox(deviceType.boundingBox as number[][])
        : (deviceItem.tileSize ?? [1, 1]);

    resultPolygon = createSchematicTilePolygon(
      deviceItem.coordinate as [number, number],
      deviceItem.direction as [number, number],
      schematicTileSize
    );
  }
  if (resultPolygon == null) {
    return null;
  }

  const color = DEVICES_TYPES_SETTINGS[deviceItem.deviceCategory]?.color || "#000000";

  let trueSizePolygon: number[][] | undefined;
  if (deviceType != null && showTrueSize) {
    trueSizePolygon = getRotatedBoundingBox({
      boundingBox: deviceType.boundingBox,
      coordinate: deviceItem.coordinate as [number, number],
      direction: deviceItem.direction as [number, number],
    });
  }

  const pixPolygon: IPixPolygon = {
    id: deviceItem._id as string,
    points: trueSizePolygon ?? resultPolygon,
    fillColor: color,
    strokeColor: "cyan",
    strokeWidth: selectedDeviceId === deviceItem._id ? "10" : "0.05",
    text: {
      show: true,
      values: isLocked ? ["LÅST"] : [getDeviceText(deviceItem)],
      color: "black",
      fontSize: "1",
    },
    tooltip: getDeviceTooltip(deviceItem, deviceType),
    isMoveable: isLocked ? false : isMoveable,
    isDragSelectable: false,
    rightClickMenu:
      deviceItem.deviceCategory === DeviceCategoryEnum.SUPPLY_AIR_DEVICE
        ? rightClickMenuWithRotate
        : rightClicMenuWithoutRotate,
  };

  return pixPolygon;
};

const formatAirFlow = (value: number | undefined): string | null => {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }

  return `${Math.round(value * 10) / 10} m³/h`;
};

const getDeviceTooltip = (deviceItem: IDevice, deviceType?: IDeviceType): string => {
  const compCode = deviceItem.compCode?.trim() || "-";
  const name = deviceItem.description?.trim() || deviceType?.typeName?.trim();
  const title = [compCode, name].filter(Boolean).join(" ");
  const airFlowLines = [
    formatAirFlow(deviceItem.supplyAirFlow) != null
      ? `Tilluft: ${formatAirFlow(deviceItem.supplyAirFlow)}`
      : null,
    formatAirFlow(deviceItem.returnAirFlow) != null
      ? `Avtrekk: ${formatAirFlow(deviceItem.returnAirFlow)}`
      : null,
  ].filter((line): line is string => line != null);

  return [title, ...airFlowLines].filter(Boolean).join("\n");
};

const isNotNull = <T>(v: T | null | undefined): v is T => v != null;

export const convertDevicesToPixPolygons = (args: IArgsConvertDevice): IPixPolygon[] => {
  if (args.devices == null || args.devices.length === 0 || args.hideDevices) {
    return [];
  }

  return args.devices
    .map((deviceItem) =>
      convertSingleDeviceToPixPolygon({
        deviceItem,
        typeDevices: args.typeDevices,
        selectedDeviceId: args.selectedDeviceId,
        showTrueSize: args.showTrueSize,
        isMoveable: args.isMoveable,
        room: args.rooms?.[deviceItem.roomNumber],
      })
    )
    .filter(isNotNull)
    .filter((pixPolygon) => viewerDeviceHasValidPolygons(pixPolygon));
};

export const convertGenereicMlResultToPixPolygon = (
  mlPredictedDevice: IMLPredictDevice
): IPixPolygon => {
  const color = DEVICES_TYPES_SETTINGS[mlPredictedDevice.deviceCategory]?.color || "#000000";
  const box = getSqaureAroundCoordinate(mlPredictedDevice.coordinate, mlPredictedDevice.direction);
  return {
    id: generateRandomString(10),
    points: box,
    fillColor: color,
    strokeColor: "cyan",
    strokeWidth: "0.05",
    text: {
      show: true,
      values: [],
      color: "black",
      fontSize: "1",
    },
    tooltip: undefined, //, getTooltip(),
    isMoveable: false,
    isDragSelectable: false,
  };
};

export const viewerDeviceHasValidPolygons = (vDevice: IPixPolygon): boolean => {
  if (!vDevice?.points) {
    return false;
  }
  return vDevice?.points?.every((polygon) => {
    return polygon.every((coord) => typeof coord === "number");
  });
};

export const getDeviceText = (deviceItem: IDevice): string => {
  if (typeof deviceItem.compCode === "string") {
    return deviceItem.compCode;
  }
  return "-";
};

export const convertDevicesToPixPaths = (devices: IDevice[]): IPixPath[] => {
  return devices.flatMap((device) => {
    if (device.grid != null) {
      return getPixPathForCeilingGrid(device.grid);
    }
    return [];
  });
};

export const getViewerPathAirTerminalDirectionsFromDevices = (
  devices: IDevice[],
  hideDevices: boolean
): IPixPath[] => {
  if (devices == null || hideDevices) {
    return [];
  }
  return devices.reduce((acc, device) => {
    const deviceSetting = DEVICES_TYPES_SETTINGS[device.deviceCategory];
    if (deviceSetting?.showDirectionInViewer !== true) return acc;
    const vectorPath = createNormalVectorPath(device.coordinate, device.direction);
    if (vectorPath == null) return acc;
    acc.push(vectorPath);
    return acc;
  }, [] as IPixPath[]);
};

export const copyDeviceAndMoveOneSquare = (device: IDevice) => {
  if (device.coordinate == null) {
    return;
  }
  const newDevice = { ...device, _id: undefined }; // Kopiera enheten och ta bort _id
  const maybeZ = typeof device.coordinate[2] === "number" ? device.coordinate[2] : undefined;
  newDevice.coordinate =
    maybeZ == null
      ? [device.coordinate[0] + 0.6, device.coordinate[1]]
      : [device.coordinate[0] + 0.6, device.coordinate[1], maybeZ];
  newDevice.boundingBox = getSqaureAroundCoordinate(
    newDevice.coordinate,
    newDevice.direction as [number, number]
  ) as IBoundingBox;
  return newDevice;
};

export interface IPointAPointB {
  pointA: {
    x: number;
    y: number;
  };
  pointB: {
    x: number;
    y: number;
  };
}

export const calculateTileBoundingBoxFromCoordinateAndDirection = (
  pointAPointB: IPointAPointB
): IBoundingBox => {
  // Create a boundingbox, 600x600mm, with the insertion point as the center. Rotate it to vector from insertionPoint with direction
  const halfSize = 0.3;
  const x = pointAPointB.pointA.x;
  const y = pointAPointB.pointA.y;
  const x1 = x + halfSize;
  const x2 = x - halfSize;
  const y1 = y + halfSize;
  const y2 = y - halfSize;
  const boundingBox: IBoundingBox = [
    [x1, y1],
    [x2, y1],
    [x2, y2],
    [x1, y2],
    [x1, y1],
  ];
  //Rotate the bounding box to the direction, created from a vector from insertionPoint to insertionPoint + direction
  const directionInsertionVector = [pointAPointB.pointB.x - x, pointAPointB.pointB.y - y];
  const angle = Math.atan2(directionInsertionVector[1], directionInsertionVector[0]);
  return boundingBox.map((point) => {
    const x = point[0] - pointAPointB.pointA.x;
    const y = point[1] - pointAPointB.pointA.y;
    const xRot = x * Math.cos(angle) - y * Math.sin(angle);
    const yRot = x * Math.sin(angle) + y * Math.cos(angle);
    return [xRot + pointAPointB.pointA.x, yRot + pointAPointB.pointA.y];
  }) as IBoundingBox;
};

export const getPointASubPointB = (pointAPointB: IPointAPointB): IDirectionDepricated => {
  return [
    pointAPointB.pointB.x - pointAPointB.pointA.x,
    pointAPointB.pointB.y - pointAPointB.pointA.y,
  ];
};

export const convertAVectorTo60cm = (vector: IDirectionDepricated): IDirectionDepricated => {
  const length = Math.sqrt(vector[0] ** 2 + vector[1] ** 2);
  const factor = 0.6 / length;
  return [vector[0] * factor, vector[1] * factor];
};

export const moveBoundingBox = (
  boundingBox: IBoundingBox,
  newInsertionPoint: { x: number; y: number }
): IBoundingBox => {
  const xDiff = newInsertionPoint.x - boundingBox[0][0];
  const yDiff = newInsertionPoint.y - boundingBox[0][1];
  return boundingBox.map((point) => [point[0] + xDiff, point[1] + yDiff]) as IBoundingBox;
};
